<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { parseAPIErrorResponse } from 'dashboard/store/utils/api';
import WahaSessionsAPI from 'dashboard/api/wahaSessions';
import SettingsAccordion from 'dashboard/components-next/Settings/SettingsAccordion.vue';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import NextSelect from 'dashboard/components-next/select/Select.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import WahaToggleList from './WahaToggleList.vue';

// WAHA session config (GOWS/NOWEB) and the WAHA Chatwoot app config of the inbox,
// saved together: Chatwoot keeps them and pushes both to WAHA.
const props = defineProps({
  session: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['saved']);

const { t } = useI18n();

const GOWS_STORAGE_KEYS = [
  'messages',
  'groups',
  'chats',
  'labels',
  'contacts',
  'message_secrets',
];
const IGNORE_KEYS = ['groups', 'channels', 'status', 'broadcast'];
const CONVERSATION_STATUSES = ['open', 'pending', 'snoozed', 'resolved'];
const LOCALES = [
  ['ar-AE', 'Arabic'],
  ['bn-BD', 'Bengali'],
  ['de-DE', 'German'],
  ['en-US', 'English'],
  ['es-ES', 'Spanish'],
  ['fa-IR', 'Persian'],
  ['fr-FR', 'French'],
  ['he-IL', 'Hebrew'],
  ['hi-IN', 'Hindi'],
  ['id-ID', 'Indonesian'],
  ['it-IT', 'Italian'],
  ['pa-PK', 'Punjabi'],
  ['pt-BR', 'Portuguese'],
  ['ru-RU', 'Russian'],
  ['tr-TR', 'Turkish'],
  ['uk-UA', 'Ukrainian'],
  ['ur-PK', 'Urdu'],
  ['zh-CN', 'Chinese Simplified'],
];
const DEVICES = ['Ubuntu', 'Windows', 'Mac OS'];
const BROWSERS = ['Chrome', 'Firefox', 'Safari', 'Edge', 'Opera'];

const isSaving = ref(false);
const isSyncing = ref(false);

const form = reactive({
  debug: false,
  client_device_name: '',
  client_browser_name: '',
  ignore: {},
  gows_storage: {},
  proxy_server: '',
  proxy_username: '',
  proxy_password: '',
  app_locale: 'en-US',
  app_link_preview: 'OFF',
  app_outgoing: 'private-note',
  app_conversations_sort: 'created_newest',
  app_conversations_status: [],
  app_flags: {},
  metadata: '{}',
  app_templates: '{}',
});

const fill = session => {
  form.debug = Boolean(session.debug);
  form.client_device_name = session.client_device_name || '';
  form.client_browser_name = session.client_browser_name || '';
  form.ignore = {
    groups: Boolean(session.ignore_groups),
    channels: session.ignore_channels ?? true,
    status: session.ignore_status ?? true,
    broadcast: session.ignore_broadcast ?? true,
  };
  form.gows_storage = Object.fromEntries(
    GOWS_STORAGE_KEYS.map(key => [key, session.gows_storage?.[key] !== false])
  );
  form.proxy_server = session.proxy_server || '';
  form.proxy_username = session.proxy_username || '';
  form.proxy_password = '';
  form.app_locale = session.app_locale || 'en-US';
  form.app_link_preview = session.app_link_preview || 'OFF';
  const conversations = session.app_conversations || {};
  form.app_outgoing = conversations.outgoing || 'private-note';
  form.app_conversations_sort = conversations.sort || 'created_newest';
  form.app_conversations_status = (conversations.status || []).filter(Boolean);
  form.app_flags = {
    mark_as_read: conversations.mark_as_read ?? true,
    sync_message_status: Boolean(conversations.sync_message_status),
    commands_server: session.app_commands?.server ?? true,
    commands_queue: Boolean(session.app_commands?.queue),
  };
  form.metadata = JSON.stringify(session.metadata || {}, null, 2);
  form.app_templates = JSON.stringify(session.app_templates || {}, null, 2);
};

watch(() => props.session, fill, { immediate: true });

const engine = computed(() => props.session.engine || '');

const options = (values, labelFor) =>
  values.map(value => ({ value, label: labelFor(value) }));

// A value saved by hand keeps showing even if it is not in the list
const withCurrent = (list, value) => {
  if (!value || list.some(option => option.value === value)) return list;
  return [
    { value, label: `${value} (${t('INBOX_MGMT.WAHA.CURRENT_VALUE')})` },
    ...list,
  ];
};

const deviceOptions = computed(() =>
  withCurrent(
    [
      { value: '', label: t('INBOX_MGMT.WAHA.DEFAULT_OPTION') },
      ...options(DEVICES, value => value),
    ],
    form.client_device_name
  )
);
const browserOptions = computed(() =>
  withCurrent(
    [
      { value: '', label: t('INBOX_MGMT.WAHA.DEFAULT_OPTION') },
      ...options(BROWSERS, value => value),
    ],
    form.client_browser_name
  )
);
const localeOptions = computed(() =>
  withCurrent(
    LOCALES.map(([value, name]) => ({ value, label: `${name} (${value})` })),
    form.app_locale
  )
);
const linkPreviewOptions = computed(() =>
  options(['OFF', 'LG', 'HG'], value =>
    t(`INBOX_MGMT.WAHA.APP.LINK_PREVIEW_OPTIONS.${value}`)
  )
);
const outgoingOptions = computed(() =>
  options(['private-note', 'message'], value =>
    t(`INBOX_MGMT.WAHA.APP.OUTGOING_OPTIONS.${value}`)
  )
);
const sortOptions = computed(() =>
  options(
    ['created_newest', 'created_oldest', 'activity_newest', 'activity_oldest'],
    value => t(`INBOX_MGMT.WAHA.APP.CONVERSATION_SORT_OPTIONS.${value}`)
  )
);

const generalFlags = computed({
  get: () => ({ debug: form.debug }),
  set: flags => {
    form.debug = flags.debug;
  },
});
const generalItems = computed(() => [
  {
    key: 'debug',
    label: t('INBOX_MGMT.WAHA.ADVANCED.DEBUG'),
    description: t('INBOX_MGMT.WAHA.ADVANCED.DEBUG_DESC'),
  },
]);
const ignoreItems = computed(() =>
  IGNORE_KEYS.map(key => ({
    key,
    label: t(`INBOX_MGMT.WAHA.ADVANCED.IGNORE_${key.toUpperCase()}`),
  }))
);
const storageItems = computed(() =>
  GOWS_STORAGE_KEYS.map(key => ({
    key,
    label: t(`INBOX_MGMT.WAHA.GOWS_STORAGE.${key.toUpperCase()}.LABEL`),
    description: t(`INBOX_MGMT.WAHA.GOWS_STORAGE.${key.toUpperCase()}.DESC`),
  }))
);
const appItems = computed(() => [
  {
    key: 'mark_as_read',
    label: t('INBOX_MGMT.WAHA.APP.MARK_AS_READ'),
    description: t('INBOX_MGMT.WAHA.APP.MARK_AS_READ_DESC'),
  },
  {
    key: 'sync_message_status',
    label: t('INBOX_MGMT.WAHA.APP.SYNC_MESSAGE_STATUS'),
    description: t('INBOX_MGMT.WAHA.APP.SYNC_MESSAGE_STATUS_DESC'),
  },
  {
    key: 'commands_server',
    label: t('INBOX_MGMT.WAHA.APP.COMMANDS_SERVER'),
    description: t('INBOX_MGMT.WAHA.APP.COMMANDS_SERVER_DESC'),
  },
  {
    key: 'commands_queue',
    label: t('INBOX_MGMT.WAHA.APP.COMMANDS_QUEUE'),
    description: t('INBOX_MGMT.WAHA.APP.COMMANDS_QUEUE_DESC'),
  },
]);
const statusFlags = computed({
  get: () =>
    Object.fromEntries(
      CONVERSATION_STATUSES.map(value => [
        value,
        form.app_conversations_status.includes(value),
      ])
    ),
  set: flags => {
    form.app_conversations_status = CONVERSATION_STATUSES.filter(
      value => flags[value]
    );
  },
});
const statusItems = computed(() =>
  CONVERSATION_STATUSES.map(key => ({
    key,
    label: t(`INBOX_MGMT.WAHA.APP.CONVERSATION_STATUS_OPTIONS.${key}`),
  }))
);

const parseJson = (value, errorKey) => {
  try {
    return value.trim() ? JSON.parse(value) : {};
  } catch {
    useAlert(t(errorKey));
    return null;
  }
};

const save = async () => {
  const metadata = parseJson(
    form.metadata,
    'INBOX_MGMT.WAHA.ADVANCED.METADATA_PARSE_ERROR'
  );
  if (metadata === null) return;
  const templates = parseJson(
    form.app_templates,
    'INBOX_MGMT.WAHA.APP.TEMPLATES_PARSE_ERROR'
  );
  if (templates === null) return;

  isSaving.value = true;
  try {
    const { data } = await WahaSessionsAPI.updateConfig(props.session.id, {
      debug: form.debug,
      metadata,
      ignore_groups: form.ignore.groups,
      ignore_channels: form.ignore.channels,
      ignore_status: form.ignore.status,
      ignore_broadcast: form.ignore.broadcast,
      gows_storage: form.gows_storage,
      proxy_server: form.proxy_server,
      proxy_username: form.proxy_username,
      proxy_password: form.proxy_password,
      client_device_name: form.client_device_name,
      client_browser_name: form.client_browser_name,
      app_locale: form.app_locale,
      app_link_preview: form.app_link_preview,
      app_templates: templates,
      app_commands: {
        server: form.app_flags.commands_server,
        queue: form.app_flags.commands_queue,
      },
      app_conversations: {
        sort: form.app_conversations_sort,
        status: form.app_conversations_status,
        mark_as_read: form.app_flags.mark_as_read,
        sync_message_status: form.app_flags.sync_message_status,
        outgoing: form.app_outgoing,
      },
    });
    emit('saved', data);
    useAlert(t('INBOX_MGMT.WAHA.ADVANCED.CONFIG_SAVED'));
  } catch (error) {
    useAlert(
      parseAPIErrorResponse(error) || t('INBOX_MGMT.WAHA.ADVANCED.CONFIG_ERROR')
    );
  } finally {
    isSaving.value = false;
  }
};

// Settings changed in the WAHA dashboard win over the ones saved here
const syncFromWaha = async () => {
  isSyncing.value = true;
  try {
    const { data } = await WahaSessionsAPI.syncConfig(props.session.id);
    emit('saved', data.session);
    useAlert(t('INBOX_MGMT.WAHA.ADVANCED.SYNC_SUCCESS'));
  } catch (error) {
    useAlert(
      parseAPIErrorResponse(error) || t('INBOX_MGMT.WAHA.ADVANCED.SYNC_ERROR')
    );
  } finally {
    isSyncing.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col">
    <SettingsAccordion :title="$t('INBOX_MGMT.WAHA.SECTIONS.GENERAL')">
      <WahaToggleList v-model="generalFlags" :items="generalItems" />
      <SettingsFieldSection
        :label="$t('INBOX_MGMT.WAHA.ADVANCED.CLIENT_DEVICE_NAME')"
        :help-text="$t('INBOX_MGMT.WAHA.ADVANCED.CLIENT_DESC')"
      >
        <NextSelect
          v-model="form.client_device_name"
          class="w-full"
          :options="deviceOptions"
        />
      </SettingsFieldSection>
      <SettingsFieldSection
        :label="$t('INBOX_MGMT.WAHA.ADVANCED.CLIENT_BROWSER_NAME')"
      >
        <NextSelect
          v-model="form.client_browser_name"
          class="w-full"
          :options="browserOptions"
        />
      </SettingsFieldSection>
    </SettingsAccordion>

    <SettingsAccordion :title="$t('INBOX_MGMT.WAHA.ADVANCED.IGNORE_FILTERS_TITLE')">
      <p class="text-sm text-n-slate-11">
        {{ $t('INBOX_MGMT.WAHA.ADVANCED.IGNORE_FILTERS_SUBTITLE') }}
      </p>
      <WahaToggleList v-model="form.ignore" :items="ignoreItems" />
    </SettingsAccordion>

    <SettingsAccordion :title="$t('INBOX_MGMT.WAHA.SECTIONS.ENGINE')">
      <template v-if="engine === 'NOWEB'">
        <p class="text-sm text-n-slate-11">
          {{ $t('INBOX_MGMT.WAHA.ADVANCED.ENGINE_SETTINGS_SUBTITLE') }}
          {{ $t('INBOX_MGMT.WAHA.ADVANCED.ENGINE_SETTINGS_LOCKED') }}
        </p>
      </template>
      <template v-else>
        <p class="text-sm text-n-slate-11">
          {{ $t('INBOX_MGMT.WAHA.GOWS_STORAGE.DESCRIPTION') }}
          <template v-if="!engine">
            {{ $t('INBOX_MGMT.WAHA.GOWS_STORAGE.ENGINE_UNKNOWN') }}
          </template>
        </p>
        <WahaToggleList v-model="form.gows_storage" :items="storageItems" />
      </template>
    </SettingsAccordion>

    <SettingsAccordion
      :title="$t('INBOX_MGMT.WAHA.SECTIONS.PROXY')"
      :default-open="Boolean(session.proxy_server)"
    >
      <SettingsFieldSection
        :label="$t('INBOX_MGMT.WAHA.ADVANCED.PROXY_SERVER')"
        :help-text="$t('INBOX_MGMT.WAHA.ADVANCED.PROXY_SERVER_HELP')"
      >
        <Input v-model="form.proxy_server" placeholder="proxy.example.com:3128" />
      </SettingsFieldSection>
      <SettingsFieldSection :label="$t('INBOX_MGMT.WAHA.ADVANCED.PROXY_USERNAME')">
        <Input v-model="form.proxy_username" />
      </SettingsFieldSection>
      <SettingsFieldSection :label="$t('INBOX_MGMT.WAHA.ADVANCED.PROXY_PASSWORD')">
        <Input
          v-model="form.proxy_password"
          type="password"
          autocomplete="new-password"
          :placeholder="
            session.proxy_password_set
              ? $t('INBOX_MGMT.WAHA.ADVANCED.PROXY_PASSWORD_SET')
              : ''
          "
        />
      </SettingsFieldSection>
    </SettingsAccordion>

    <SettingsAccordion :title="$t('INBOX_MGMT.WAHA.APP.TITLE')">
      <p class="text-sm text-n-slate-11">
        {{ $t('INBOX_MGMT.WAHA.APP.SUBTITLE') }}
      </p>
      <SettingsFieldSection :label="$t('INBOX_MGMT.WAHA.APP.LOCALE')">
        <NextSelect
          v-model="form.app_locale"
          class="w-full"
          :options="localeOptions"
        />
      </SettingsFieldSection>
      <SettingsFieldSection :label="$t('INBOX_MGMT.WAHA.APP.LINK_PREVIEW')">
        <NextSelect
          v-model="form.app_link_preview"
          class="w-full"
          :options="linkPreviewOptions"
        />
      </SettingsFieldSection>
      <SettingsFieldSection
        :label="$t('INBOX_MGMT.WAHA.APP.OUTGOING')"
        :help-text="$t('INBOX_MGMT.WAHA.APP.OUTGOING_DESC')"
      >
        <NextSelect
          v-model="form.app_outgoing"
          class="w-full"
          :options="outgoingOptions"
        />
      </SettingsFieldSection>
      <SettingsFieldSection :label="$t('INBOX_MGMT.WAHA.APP.CONVERSATION_SORT')">
        <NextSelect
          v-model="form.app_conversations_sort"
          class="w-full"
          :options="sortOptions"
        />
      </SettingsFieldSection>
      <SettingsFieldSection
        :label="$t('INBOX_MGMT.WAHA.APP.CONVERSATION_STATUS')"
        :help-text="$t('INBOX_MGMT.WAHA.APP.CONVERSATION_STATUS_DESC')"
      >
        <WahaToggleList v-model="statusFlags" :items="statusItems" />
      </SettingsFieldSection>
      <WahaToggleList v-model="form.app_flags" :items="appItems" />
    </SettingsAccordion>

    <SettingsAccordion
      :title="$t('INBOX_MGMT.WAHA.SECTIONS.ADVANCED')"
      :default-open="false"
    >
      <TextArea
        v-model="form.metadata"
        :label="$t('INBOX_MGMT.WAHA.ADVANCED.METADATA')"
        auto-height
        :max-length="4000"
        custom-text-area-wrapper-class="min-h-[8rem] font-mono"
      />
      <TextArea
        v-model="form.app_templates"
        :label="$t('INBOX_MGMT.WAHA.APP.TEMPLATES')"
        auto-height
        :max-length="4000"
        custom-text-area-wrapper-class="min-h-[8rem] font-mono"
      />
    </SettingsAccordion>

    <div class="flex flex-wrap gap-2 pt-4 border-t border-n-weak">
      <NextButton
        :is-loading="isSaving"
        :label="$t('INBOX_MGMT.WAHA.ADVANCED.SAVE')"
        @click="save"
      />
      <NextButton
        faded
        slate
        icon="i-lucide-download"
        :is-loading="isSyncing"
        :label="$t('INBOX_MGMT.WAHA.ADVANCED.SYNC_FROM_WAHA')"
        @click="syncFromWaha"
      />
    </div>
  </div>
</template>
